import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import UpdatePaymentMethodModal from './UpdatePaymentMethodModal';
import SavedCardsModal from './SavedCardsModal';
import EditBillingAddressModalNew from './EditBillingAddressModalNew';
import PauseSubscriptionModal from './PauseSubscriptionModal';
import ChangeFrequency from '../../PlansPage/ChangeFrequency';
import { useStripe } from '../../../contexts/StripeContext';
import BillingOverview from './components/BillingOverview';
import PaymentMethodCard from './components/PaymentMethodCard';
import PlanManagementCard from './components/PlanManagementCard';
import PaymentHistoryTable from './components/PaymentHistoryTable';
import './PlanBilling.css';
import './SavedCardsModal.css';
import './EditBillingAddressModal.css';

const PlanAndBilling = ({
  // Organization data
  organizationData,
  isSuperAdmin,
  orgId,

  // Subscription data
  subscriptionDetails,
  billingDetails,
  cardDetails,
  paymentHistory,
  subscriptionActive,
  subscriptionExpiredorPaymentFailed,

  // Loading states
  isLoadingBilling,
  isProcessingPayment,
  isLoadingPayments,
  isProcessingSubscriptionChange,

  // Modal states
  isPaymentMethodModalOpen,
  setIsPaymentMethodModalOpen,
  isSubscriptionModalOpen,
  setIsSubscriptionModalOpen,
  isFrequencyModalOpen,
  setIsFrequencyModalOpen,
  isMakePaymentModalOpen,
  setIsMakePaymentModalOpen,

  // Search state
  searchTerm,
  setSearchTerm,

  // Event handlers
  handleToggleSubscription,
  handleSearch,
  handleClearSearch,
  handlePaymentMethodChanged,
  handleFrequencyChanged,
  handleDeletePaymentMethod,
  handleMakePayment,
}) => {
  const { stripePromise } = useStripe();

  // Local state for new modals
  const [isSavedCardsModalOpen, setIsSavedCardsModalOpen] = useState(false);
  const [isEditBillingAddressModalOpen, setIsEditBillingAddressModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const { base_subscription_failed } = subscriptionDetails || {};

  const isSubInactive = !subscriptionActive;
  const hasBaseFailure = base_subscription_failed;

  const navigate = useNavigate();

  // Debug logging for organization data
  useEffect(() => {
    if (organizationData) {
      console.log('PlanAndBilling - Organization Data:', {
        stripe_customer_id: organizationData.stripe_customer_id,
        user_name: organizationData.user_name,
        org_name: organizationData.org_name,
      });
    }
  }, [organizationData]);

  // UI Helper functions
  const formatPrice = (amount) => (amount / 100).toFixed(0);

  const getSubscriptionIssueMessage = () => {
    if (isSubInactive) {
      return 'Your subscription is currently inactive. Please make a payment to continue using the platform.';
    }
    if (hasBaseFailure) {
      return 'Your base subscription has failed. Please make a payment to restore full access.';
    }
    return '';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) return 'Invalid Date';

    try {
      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    } catch (e) {
      // fallback
      return date.toDateString();
    }
  };

  const getRemainingDays = (validUpto) => {
    if (!validUpto) return 'N/A';

    const today = new Date();
    const validDate = new Date(validUpto);

    const millisecondsDiff = validDate.getTime() - today.getTime();

    const remainingDays = Math.max(
      0,
      Math.ceil(millisecondsDiff / (1000 * 60 * 60 * 24)),
    );

    return remainingDays ? `${remainingDays} days left` : 'Expired';
  };

  const formatExpiry = (expiryMonth, expiryYear) => {
    if (!expiryMonth || !expiryYear) return '';
    if (expiryMonth > 9) return `${expiryMonth}/${expiryYear}`;
    return `0${expiryMonth}/${expiryYear}`;
  };

  const subscriptionValidityLabel = (subscriptionDetails) => {
    if (isSubInactive) {
      return 'Subscription Expired';
    }
    if (subscriptionActive) {
      return 'Next Payment Date';
    }
    if (subscriptionDetails?.status === 'trialing') {
      return 'Trial Active Until';
    }
    return 'Subscription Active Until';
  };

  const getTrialRemainderMessage = () => {
    if (subscriptionActive) {
      return `You're currently on a ${billingDetails?.plan_name ?? ''} trial. Your paid subscription will begin automatically on ${formatDate(billingDetails?.plan_valid_upto)}.`;
    }

    return `Your ${billingDetails?.plan_name ?? ''} trial is active and will end on ${formatDate(billingDetails?.plan_valid_upto)}. To continue using PrescriptSure, please select a plan before your trial expires.`;
  };

  // Event handlers
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleChangePlan = () => {
    if (isSuperAdmin && orgId) {
      navigate(`/super-admin/${orgId}/change-plan`, {
        state: { organization: organizationData, isSuperAdmin },
      });
    } else {
      navigate('/settings/change-plan', {
        state: { organization: organizationData },
      });
    }
  };

  // Saved Cards Modal handlers
  const handleAddNewCard = () => {
    setIsSavedCardsModalOpen(false);
    setIsPaymentMethodModalOpen(true);
  };

  const handlePaymentMethodModalClose = (shouldReopenSavedCards = false) => {
    setIsPaymentMethodModalOpen(false);
    if (shouldReopenSavedCards) {
      // Small delay to ensure modal transition
      setTimeout(() => {
        setIsSavedCardsModalOpen(true);
      }, 300);
    }
  };

  const handleEditBillingAddress = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setIsSavedCardsModalOpen(false);
    setIsEditBillingAddressModalOpen(true);
  };

  const handleBillingAddressUpdated = () => {
    handlePaymentMethodChanged();
    setIsEditBillingAddressModalOpen(false);
    setSelectedPaymentMethod(null);
    setTimeout(() => setIsSavedCardsModalOpen(true), 300);
  };

  const handleSavedCardsModalClose = () => {
    setIsSavedCardsModalOpen(false);
  };

  const handleEditBillingAddressModalClose = () => {
    setIsEditBillingAddressModalOpen(false);
    setSelectedPaymentMethod(null);
    setIsSavedCardsModalOpen(true); // Reopen saved cards modal
  };

  return (
    <div className="billing-container">
      {/* Billing Overview Section */}
      <BillingOverview
        subscriptionDetails={subscriptionDetails}
        billingDetails={billingDetails}
        subscriptionActive={subscriptionActive}
      />

      {/* Payment Method Card */}
      <PaymentMethodCard
        cardDetails={cardDetails}
        onChangeClick={() => setIsSavedCardsModalOpen(true)}
      />

      {/* Plan Management Card */}
      <PlanManagementCard
        billingDetails={billingDetails}
        subscriptionDetails={subscriptionDetails}
        subscriptionActive={subscriptionActive}
        isSuperAdmin={isSuperAdmin}
        onChangePlan={handleChangePlan}
        onChangeFrequency={() => setIsFrequencyModalOpen(true)}
        onPauseSubscription={() => setIsSubscriptionModalOpen(true)}
      />

      {/* Payment History Table */}
      <PaymentHistoryTable
        paymentHistory={paymentHistory}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />

      {/* Payment Method Change Modal */}
      <Modal
        show={isPaymentMethodModalOpen}
        onHide={() => setIsPaymentMethodModalOpen(false)}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
        className="cst-modal"
      >
        <Elements stripe={stripePromise}>
          <UpdatePaymentMethodModal
            isOpen={isPaymentMethodModalOpen}
            onClose={handlePaymentMethodModalClose}
            stripeCustomerId={organizationData?.stripe_customer_id}
            onPaymentMethodChanged={handlePaymentMethodChanged}
            organizationData={organizationData}
          />
        </Elements>
      </Modal>

      {/* Frequency Change Modal */}
      <ChangeFrequency
        isOpen={isFrequencyModalOpen}
        onClose={() => setIsFrequencyModalOpen(false)}
        organization={organizationData?.user_name}
        currentInterval={billingDetails?.interval}
        stripeSubscriptionId={subscriptionDetails?.id}
        currentPlanId={subscriptionDetails?.plan_id}
        onFrequencyChanged={handleFrequencyChanged}
        discount={billingDetails?.discount}
      />

      {/* Pause/Resume Subscription Modal */}
      <PauseSubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        subscriptionActive={subscriptionActive}
        isProcessing={isProcessingSubscriptionChange}
        onConfirm={handleToggleSubscription}
        billingEndDate={billingDetails?.current_period_end}
      />

      {/* Make Payment Modal */}
      <Modal
        show={isMakePaymentModalOpen}
        onHide={() => setIsMakePaymentModalOpen(false)}
        backdrop="static"
        keyboard={false}
        centered
        className="cst-modal"
      >
        <Modal.Header>
          <Modal.Title className="text-primary" as="h5">
            Make Payment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{getSubscriptionIssueMessage()}</p>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              style={{ padding: '8px 15px' }}
              disabled={isProcessingPayment}
              onClick={() => setIsMakePaymentModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleMakePayment}
              disabled={isProcessingPayment}
            >
              {isProcessingPayment ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : (
                'Pay Now'
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Saved Cards Management Modal */}
      <Modal
        show={isSavedCardsModalOpen}
        onHide={handleSavedCardsModalClose}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
        className="cst-modal"
      >
        <Elements stripe={stripePromise}>
          <SavedCardsModal
            isOpen={isSavedCardsModalOpen}
            onClose={handleSavedCardsModalClose}
            stripeCustomerId={organizationData?.stripe_customer_id}
            savedPaymentMethods={organizationData?.saved_payment_methods || []}
            defaultPaymentMethodId={organizationData?.default_payment_method_id}
            onPaymentMethodChanged={handlePaymentMethodChanged}
            onDeletePaymentMethod={handleDeletePaymentMethod}
            onAddNewCard={handleAddNewCard}
            onEditBillingAddress={handleEditBillingAddress}
          />
        </Elements>
      </Modal>

      {/* Edit Billing Address Modal */}
      <Modal
        show={isEditBillingAddressModalOpen}
        onHide={handleEditBillingAddressModalClose}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
        className="cst-modal"
      >
        <Elements stripe={stripePromise}>
          <EditBillingAddressModalNew
            isOpen={isEditBillingAddressModalOpen}
            onClose={handleEditBillingAddressModalClose}
            paymentMethod={selectedPaymentMethod}
            onAddressUpdated={handleBillingAddressUpdated}
            stripeCustomerId={organizationData?.stripe_customer_id}
          />
        </Elements>
      </Modal>
    </div>
  );
};

export default PlanAndBilling;
